import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, Divider, LinearProgress, MenuItem,
  Select, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import { collection, onSnapshot } from 'firebase/firestore'
import { useSearchParams } from 'react-router-dom'
import * as XLSX from 'xlsx'
import { db } from '../firebase'

const DEFAULT_SURVEY_CODE = 'creon_consumers_ru_en'
const QUESTION_TITLES = {
  S1: 'Cinsiyet', S2: 'Yaş', S3: 'Yaşanılan şehir', S4: 'Hane gelir düzeyi',
  S9: 'Kullanılan enzim ürünleri', Q1: 'Kullanım sıklığı', Q2: 'Kullanım durumları',
  ET1_2: 'Rafta ilk fark edilen ürün', ET1_3: 'Rafta en çok dikkat çeken ürün',
  ET1_4: 'İlk değerlendirilecek ürün', C1: 'Genel paket tercihi',
  C4: 'Satın alma olasılığı en yüksek paket', C5: 'Modern tasarım ve tanınırlık',
  C6: 'Gelecekte tercih edilen paket', C7: 'Korunması önemli unsurlar', C8: 'Tercih nedeni',
}

function dateValue(value) {
  if (typeof value?.toDate === 'function') return value.toDate()
  return value instanceof Date ? value : null
}

function formatDate(value) {
  const date = dateValue(value)
  return date ? new Intl.DateTimeFormat('tr-TR', { dateStyle: 'medium', timeStyle: 'short' }).format(date) : '—'
}

function displayAnswer(value) {
  if (Array.isArray(value)) return value.join(', ')
  if (value && typeof value === 'object') return Object.values(value).join(', ')
  return String(value || '—')
}

function keyLabel(key) {
  return QUESTION_TITLES[key] || key.replace(/_/g, ' ')
}

function Distribution({ title, values, total }) {
  const rows = Object.entries(values).sort((a, b) => b[1] - a[1])
  if (!rows.length) return null
  return <Card variant="outlined" sx={{ borderRadius: 3, height: '100%' }}><CardContent>
    <Typography variant="subtitle1" fontWeight={800} mb={2}>{title}</Typography>
    <Stack spacing={1.3}>
      {rows.slice(0, 8).map(([label, count]) => <Box key={label}>
        <Stack direction="row" justifyContent="space-between" spacing={1} mb={0.5}>
          <Typography variant="body2" noWrap title={label}>{label}</Typography>
          <Typography variant="body2" fontWeight={700}>{count} · %{Math.round((count / total) * 100)}</Typography>
        </Stack>
        <LinearProgress variant="determinate" value={(count / total) * 100} sx={{ height: 7, borderRadius: 9 }} />
      </Box>)}
    </Stack>
  </CardContent></Card>
}

export default function SurveyReportPage() {
  const [searchParams] = useSearchParams()
  const surveyCode = searchParams.get('survey_code') || DEFAULT_SURVEY_CODE
  const [responses, setResponses] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [status, setStatus] = useState('all')
  const [selected, setSelected] = useState(null)

  useEffect(() => {
    setLoading(true); setError('')
    const surveyId = encodeURIComponent(surveyCode)
    return onSnapshot(collection(db, 'surveys', surveyId, 'responses'), snapshot => {
      setResponses(snapshot.docs.map(item => ({ id: item.id, ...item.data() })).sort((a, b) => (dateValue(b.completed_at)?.getTime() || 0) - (dateValue(a.completed_at)?.getTime() || 0)))
      setLoading(false)
    }, snapshotError => {
      console.error('Anket sonuçları yüklenemedi:', snapshotError)
      setError('Anket sonuçları yüklenemedi. Bu anketin sonuçlarını görüntüleme yetkinizi kontrol edin.')
      setLoading(false)
    })
  }, [surveyCode])

  const filtered = useMemo(() => status === 'all' ? responses : responses.filter(item => item.status === status), [responses, status])
  const completed = responses.filter(item => item.status === 'completed')
  const screened = responses.filter(item => item.status === 'screened_out')
  const distributions = useMemo(() => {
    const result = {}
    completed.forEach(response => Object.entries(response.answer_labels || {}).forEach(([key, value]) => {
      if (key.endsWith('_OTHER') || !QUESTION_TITLES[key]) return
      const values = Array.isArray(value) ? value : [value]
      values.forEach(item => { const label = displayAnswer(item); if (label !== '—') result[key] = { ...(result[key] || {}), [label]: (result[key]?.[label] || 0) + 1 } })
    }))
    return result
  }, [completed])

  const exportXlsx = () => {
    const rows = responses.map(response => ({
      'Yanıt ID': response.response_id || response.id, Durum: response.status, 'Eleme nedeni': response.screenout_reason || '',
      'Tamamlanma zamanı': formatDate(response.completed_at), Dil: response.language || '',
      ...Object.fromEntries(Object.entries(response.answer_labels || {}).map(([key, value]) => [keyLabel(key), displayAnswer(value)])),
    }))
    const book = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(book, XLSX.utils.json_to_sheet(rows), 'Yanıtlar')
    XLSX.writeFile(book, `${surveyCode}-rapor.xlsx`)
  }

  return <Box sx={{ minHeight: '100vh', p: { xs: 2, md: 4 }, background: 'linear-gradient(180deg,#f7f9fc,#eef4f8)' }}>
    <Stack spacing={3} maxWidth={1400} mx="auto">
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2}>
        <Box><Typography variant="h3" fontWeight={800}>Anket sonuç raporu</Typography><Typography color="text.secondary">{surveyCode} · Yanıtlar gerçek zamanlı güncellenir</Typography></Box>
        <Button variant="outlined" startIcon={<DownloadOutlinedIcon />} onClick={exportXlsx} disabled={!responses.length}>Excel'e aktar</Button>
      </Stack>
      {error && <Alert severity="error">{error}</Alert>}
      {loading ? <Box minHeight={300} display="grid" sx={{ placeItems: 'center' }}><CircularProgress /></Box> : <>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          {[['Toplam yanıt', responses.length, 'primary'], ['Tamamlanan', completed.length, 'success'], ['Elenen', screened.length, 'warning']].map(([label, value, color]) => <Card key={label} sx={{ flex: 1, borderRadius: 3 }}><CardContent><Typography color="text.secondary">{label}</Typography><Typography variant="h3" fontWeight={800} color={`${color}.main`}>{value}</Typography></CardContent></Card>)}
          <Card sx={{ flex: 1, borderRadius: 3 }}><CardContent><Typography color="text.secondary">Tamamlanma oranı</Typography><Typography variant="h3" fontWeight={800}>{responses.length ? `%${Math.round((completed.length / responses.length) * 100)}` : '—'}</Typography></CardContent></Card>
        </Stack>
        {!responses.length ? <Alert severity="info">Henüz bu anket için kaydedilmiş bir yanıt yok.</Alert> : <>
          <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: 'repeat(2, 1fr)' }} gap={2}>
            {Object.entries(distributions).slice(0, 8).map(([key, values]) => <Distribution key={key} title={keyLabel(key)} values={values} total={completed.length} />)}
          </Box>
          <Card variant="outlined" sx={{ borderRadius: 3 }}><CardContent>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} mb={2} spacing={1}><Typography variant="h6" fontWeight={800}>Katılımcı yanıtları</Typography><Select size="small" value={status} onChange={event => setStatus(event.target.value)} sx={{ minWidth: 160 }}><MenuItem value="all">Tüm durumlar</MenuItem><MenuItem value="completed">Tamamlananlar</MenuItem><MenuItem value="screened_out">Elenenler</MenuItem></Select></Stack>
            <Box sx={{ overflowX: 'auto' }}><Table size="small"><TableHead><TableRow><TableCell>Yanıt ID</TableCell><TableCell>Durum</TableCell><TableCell>Tarih</TableCell><TableCell>Dil</TableCell><TableCell align="right">İşlem</TableCell></TableRow></TableHead><TableBody>{filtered.map(item => <TableRow key={item.id}><TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{item.response_id || item.id}</TableCell><TableCell><Chip size="small" label={item.status === 'completed' ? 'Tamamlandı' : 'Elendi'} color={item.status === 'completed' ? 'success' : 'warning'} /></TableCell><TableCell>{formatDate(item.completed_at)}</TableCell><TableCell>{(item.language || '—').toUpperCase()}</TableCell><TableCell align="right"><Button size="small" startIcon={<VisibilityOutlinedIcon />} onClick={() => setSelected(item)}>İncele</Button></TableCell></TableRow>)}</TableBody></Table></Box>
          </CardContent></Card>
        </>}
      </>}
    </Stack>
    <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} fullWidth maxWidth="md"><DialogTitle>Yanıt ayrıntısı</DialogTitle><DialogContent dividers><Stack spacing={1.5}>{selected && <><Typography variant="body2" color="text.secondary">{selected.response_id || selected.id} · {formatDate(selected.completed_at)}</Typography><Divider />{Object.entries(selected.answer_labels || {}).map(([key, value]) => <Box key={key}><Typography variant="subtitle2" fontWeight={800}>{keyLabel(key)}</Typography><Typography variant="body2">{displayAnswer(value)}</Typography></Box>)}</>}</Stack></DialogContent><DialogActions><Button onClick={() => setSelected(null)}>Kapat</Button></DialogActions></Dialog>
  </Box>
}
